import { z } from 'zod';

const i18nRecordSchema = z
  .object({
    tr: z.string().max(2000).optional().default(''),
    en: z.string().max(2000).optional().default(''),
    de: z.string().max(2000).optional().default(''),
    fr: z.string().max(2000).optional().default(''),
    nl: z.string().max(2000).optional().default(''),
  })
  .strict();

export type I18nRecord = z.infer<typeof i18nRecordSchema>;

export type SiteSettingType = 'boolean' | 'string' | 'number' | 'json' | 'i18nRecord';

export interface SiteSettingDefinition<TSchema extends z.ZodTypeAny = z.ZodTypeAny> {
  key: string;
  type: SiteSettingType;
  schema: TSchema;
  defaultValue: z.infer<TSchema>;
  /** Settings that need a higher privilege (e.g. ops-only) can opt out of the
   * normal admin form. Currently informational only. */
  ops?: boolean;
}

function def<TSchema extends z.ZodTypeAny>(
  d: SiteSettingDefinition<TSchema>,
): SiteSettingDefinition<TSchema> {
  return d;
}

export const SITE_SETTINGS_REGISTRY = {
  maintenance_mode: def({
    key: 'maintenance_mode',
    type: 'boolean',
    schema: z.boolean(),
    defaultValue: false,
  }),
  maintenance_message: def({
    key: 'maintenance_message',
    type: 'i18nRecord',
    schema: i18nRecordSchema,
    defaultValue: { tr: '', en: '', de: '', fr: '', nl: '' },
  }),
  signup_disabled: def({
    key: 'signup_disabled',
    type: 'boolean',
    schema: z.boolean(),
    defaultValue: false,
  }),
  signup_disabled_message: def({
    key: 'signup_disabled_message',
    type: 'i18nRecord',
    schema: i18nRecordSchema,
    defaultValue: { tr: '', en: '', de: '', fr: '', nl: '' },
  }),
  banner_message: def({
    key: 'banner_message',
    type: 'i18nRecord',
    schema: i18nRecordSchema,
    defaultValue: { tr: '', en: '', de: '', fr: '', nl: '' },
  }),
  support_email: def({
    key: 'support_email',
    type: 'string',
    schema: z.string().email().max(200),
    defaultValue: 'support@gelirgider.local',
  }),
  max_upload_mb: def({
    key: 'max_upload_mb',
    type: 'number',
    schema: z.number().int().min(1).max(200),
    defaultValue: 10,
  }),
  templates_auto_hide_threshold: def({
    key: 'templates_auto_hide_threshold',
    type: 'number',
    schema: z.number().int().min(1).max(1000),
    defaultValue: 5,
  }),
  default_currency: def({
    key: 'default_currency',
    type: 'string',
    schema: z.string().regex(/^[A-Z]{3}$/, '3-letter ISO 4217 code'),
    defaultValue: 'EUR',
  }),
  feature_open_banking: def({
    key: 'feature_open_banking',
    type: 'boolean',
    schema: z.boolean(),
    defaultValue: true,
  }),
  // Stripe credentials — env'den okunan değerleri runtime'da override eder.
  // Admin UI bunları sadece ops sekmesinde gösterir; site-settings.tsx
  // ekranında gizlenir (kazara değiştirmesini önler).
  stripe_secret_key: def({
    key: 'stripe_secret_key',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  stripe_webhook_secret: def({
    key: 'stripe_webhook_secret',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  stripe_publishable_key: def({
    key: 'stripe_publishable_key',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  // ── Resend / Mail credentials ──────────────────────────────────────────
  // Boot'ta AdminMailService bunları okuyup ResendClient'i hot-reload eder.
  resend_api_key: def({
    key: 'resend_api_key',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  resend_webhook_secret: def({
    key: 'resend_webhook_secret',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  mail_from_address: def({
    key: 'mail_from_address',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  mail_from_name: def({
    key: 'mail_from_name',
    type: 'string',
    schema: z.string().max(120),
    defaultValue: 'VieBudget',
    ops: true,
  }),
  // ── S3-compatible storage credentials (R2 / AWS / MinIO) ────────────────
  // Boot'ta AdminStorageService bunları okuyup StorageService'i hot-reload
  // eder. Stripe/Resend ile aynı pattern.
  storage_access_key: def({
    key: 'storage_access_key',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  storage_secret_key: def({
    key: 'storage_secret_key',
    type: 'string',
    schema: z.string().max(500),
    defaultValue: '',
    ops: true,
  }),
  storage_bucket: def({
    key: 'storage_bucket',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  storage_endpoint: def({
    key: 'storage_endpoint',
    type: 'string',
    schema: z.string().max(500),
    defaultValue: '',
    ops: true,
  }),
  storage_region: def({
    key: 'storage_region',
    type: 'string',
    schema: z.string().max(50),
    defaultValue: 'auto',
    ops: true,
  }),
  storage_force_path_style: def({
    key: 'storage_force_path_style',
    type: 'boolean',
    schema: z.boolean(),
    defaultValue: false,
    ops: true,
  }),
  // ── AI / Anthropic (fatura okuma + insights) ────────────────────────────
  // Boot'ta ReceiptScanService bunları okuyup Anthropic client'ı hot-reload eder.
  ai_anthropic_api_key: def({
    key: 'ai_anthropic_api_key',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  ai_receipt_model: def({
    key: 'ai_receipt_model',
    type: 'string',
    schema: z.string().max(120),
    defaultValue: '',
    ops: true,
  }),
  // ── Wise / PayPal OAuth credentials ─────────────────────────────────────
  // Boot'ta AdminProviderConnectionsService bunları okuyup ilgili adapter'ı
  // hot-reload eder (Stripe/Resend/Enable Banking ile aynı pattern). ops:true →
  // genel site-settings formunda gizli, /integrations-settings sayfasından yönetilir.
  wise_client_id: def({
    key: 'wise_client_id',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  wise_client_secret: def({
    key: 'wise_client_secret',
    type: 'string',
    schema: z.string().max(500),
    defaultValue: '',
    ops: true,
  }),
  paypal_client_id: def({
    key: 'paypal_client_id',
    type: 'string',
    schema: z.string().max(200),
    defaultValue: '',
    ops: true,
  }),
  paypal_client_secret: def({
    key: 'paypal_client_secret',
    type: 'string',
    schema: z.string().max(500),
    defaultValue: '',
    ops: true,
  }),
  // PayPal ortamı — 'sandbox' | 'live'. Live app credential'ı sandbox URL'ine
  // gönderilirse "client_id incorrect" hatası olur; bu yüzden ortam seçilebilir.
  paypal_env: def({
    key: 'paypal_env',
    type: 'string',
    schema: z.enum(['sandbox', 'live']),
    defaultValue: 'sandbox',
    ops: true,
  }),
} as const;

export type SiteSettingKey = keyof typeof SITE_SETTINGS_REGISTRY;

export function isRegisteredSiteSettingKey(key: string): key is SiteSettingKey {
  return Object.prototype.hasOwnProperty.call(SITE_SETTINGS_REGISTRY, key);
}

export function getSiteSettingDefinition(key: string): SiteSettingDefinition | null {
  return isRegisteredSiteSettingKey(key) ? SITE_SETTINGS_REGISTRY[key] : null;
}

export function listSiteSettingDefinitions(): SiteSettingDefinition[] {
  return Object.values(SITE_SETTINGS_REGISTRY);
}

export type ValidateResult = { success: true; value: unknown } | { success: false; error: string };

export function validateSiteSettingValue(key: string, value: unknown): ValidateResult {
  const definition = getSiteSettingDefinition(key);
  if (!definition) return { success: false, error: 'Unknown setting key' };
  const result = definition.schema.safeParse(value);
  if (!result.success) {
    return {
      success: false,
      error: result.error.issues
        .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
        .join('; '),
    };
  }
  return { success: true, value: result.data };
}
